/* ── WORK_TRACK — Dashboard ───────────────────────────────── */

import { $, esc, icon, api, state, toast, fmtMin, daysAgoStr, loadingHtml, emptyHtml, registerView } from './core';
import { mountEffortChart } from './effort-chart';
import type { Summary, SummaryPerProject, SummaryPerTask, TimeEntry } from './types';

export async function loadDashboard(): Promise<void> {
  mountEffortChart();
  try {
    const s = await api<Summary>('/api/summary?days=14');
    $('#statToday').textContent = fmtMin(s.today_minutes);
    $('#statWeek').textContent = fmtMin(s.week_minutes);
    $('#statMonth').textContent = fmtMin(s.month_minutes);
    $('#statTotal').textContent = fmtMin(s.total_minutes);
    $('#statActive').textContent = String(s.task_counts.in_progress || 0);
    $('#statDone').textContent = String(s.task_counts.done || 0);

    renderProjectBars(s.per_project);
    renderTopTasks(s.per_task);
    loadRecentEntries();
  } catch (e) {
    toast((e as Error).message, true);
  }
}

/* ── Daily effort card is rendered by src/effort-chart.tsx (React) ── */

function renderProjectBars(perProject: SummaryPerProject[]): void {
  const total = perProject.reduce((a, p) => a + p.minutes, 0) || 1;
  const ul = $('#projectBars');
  if (!perProject.length && !state.projects.length) {
    ul.innerHTML = emptyHtml(
      icon('folder', 30),
      'No projects yet',
      'Create a project to start logging time against it.',
      {
        label: '+ New project',
        on: 'projects'
      }
    );
    return;
  }
  const withZero = state.projects
    .map(p => {
      const found = perProject.find(x => x.id === p.id);
      return found
        ? { ...found, color: p.color, icon: p.icon }
        : { id: p.id, name: p.name, color: p.color, icon: p.icon, minutes: 0 };
    })
    .filter(x => x.minutes > 0 || perProject.length === 0)
    .sort((a, b) => b.minutes - a.minutes)
    .slice(0, 8);

  ul.innerHTML = withZero
    .map(
      p => `
    <li>
      <div class="bar-row">
        <span><strong>${esc(p.name)}</strong></span>
        <span class="muted">${fmtMin(p.minutes)} · ${Math.round((p.minutes / total) * 100)}%</span>
      </div>
      <div class="bar-track"><div class="bar-fill" style="width:${Math.round((p.minutes / total) * 100)}%; background:#565051"></div></div>
    </li>`
    )
    .join('');
  if (!withZero.length)
    ul.innerHTML = emptyHtml(
      icon('timer', 30),
      'No time logged yet',
      'Start the timer or log a session to see it here.',
      {
        label: 'Go to timer',
        on: 'timer'
      }
    );
}

function renderTopTasks(perTask: SummaryPerTask[]): void {
  const ul = $('#topTasks');
  if (!perTask.length) {
    ul.innerHTML = emptyHtml(
      icon('puzzle', 30),
      'No task time tracked',
      'Log time against specific tasks to see which ones take your effort.'
    );
    return;
  }
  ul.innerHTML = perTask
    .map(
      t => `
    <li>
      <span class="item-icon">${icon('check', 18)}</span>
      <div class="item-main"><div class="item-title">${esc(t.title)}</div></div>
      <div class="item-right"><span class="item-val">${fmtMin(t.minutes)}</span></div>
    </li>`
    )
    .join('');
}

async function loadRecentEntries(): Promise<void> {
  const ul = $('#recentEntries');
  ul.innerHTML = loadingHtml();
  try {
    const entries = await api<TimeEntry[]>(`/api/entries?from=${daysAgoStr(6)}`);
    const recent = entries.slice(0, 6);
    if (!recent.length) {
      ul.innerHTML = emptyHtml(icon('clipboard', 30), 'No recent activity', 'Your logged sessions will appear here.');
      return;
    }
    ul.innerHTML = recent
      .map(
        e => `
      <li>
        <span class="item-icon">${icon('timer', 18)}</span>
        <div class="item-main">
          <div class="item-title">${esc(e.project_name || 'General')} ${e.task_title ? '· ' + esc(e.task_title) : ''}</div>
          <div class="item-sub">${esc(e.entry_date)}${e.note ? ' — ' + esc(e.note) : ''}</div>
        </div>
        <div class="item-right"><span class="item-val">${fmtMin(e.minutes)}</span></div>
      </li>`
      )
      .join('');
  } catch {
    ul.innerHTML = '<li class="empty">Could not load.</li>';
  }
}

registerView('dashboard', loadDashboard);
